import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'motion/react';
import toast from 'react-hot-toast';
import { Eye, MoreVertical, Plus, Pencil } from 'lucide-react';
import { libraryApi } from '../../lib/api/libraryApi';
import { useAuth } from '../../contexts/AuthContext';
import useBodyScrollLock from 'src/hooks/useBodyScrollLock';
import AIInterviewCreate from './AIInterviewCreate';
import RubricInterviewCreate from './RubricInterviewCreate';
import { TrashIcon } from '@heroicons/react/24/outline';
import { ADVANCED_INTERVIEW_TYPE_LABELS } from 'src/constants/advancedInterviewTypes';
import AIInterviewViewModal from 'src/components/library/AIInterviewViewModal';
import ErrorBoundary from 'src/components/ui/ErrorBoundary';

const AIInterviewLibrary = () => {
  const { user: authUser } = useAuth();

  const canDeleteLibraryAIInterview = (row) => {
    const uid = authUser?._id || authUser?.id;
    if (!uid || !row?.createdBy) return false;
    if (String(row.createdBy) === String(uid)) return true;
    return (
      authUser?.accountType === 'org' &&
      (authUser?.role === 'admin' || authUser?.role === 'org_admin')
    );
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewInterviewId, setViewInterviewId] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editLibraryInterviewId, setEditLibraryInterviewId] = useState(null);
  const [editRubricTemplateId, setEditRubricTemplateId] = useState(null);
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState(null); // { id, top, left } | null
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, title } | null
  useBodyScrollLock(createModalOpen || Boolean(deleteConfirm) || Boolean(editLibraryInterviewId) || Boolean(editRubricTemplateId));
  const queryClient = useQueryClient();

  const closeActionsMenu = useCallback(() => setActionsMenuAnchor(null), []);

  const deleteLibraryMutation = useMutation({
    mutationFn: (id) => libraryApi.deleteLibraryAIInterview(id),
    onSuccess: (_, deletedId) => {
      queryClient.invalidateQueries({ queryKey: ['library-ai-interviews-stats'] });
      toast.success('AI interview removed from your library');
      setDeleteConfirm(null);
      if (viewInterviewId && String(viewInterviewId) === String(deletedId)) {
        setViewInterviewId(null);
      }
    },
    onError: (err) => {
      const msg = err?.response?.data?.message || 'Could not delete this AI interview';
      toast.error(msg);
    },
  });

  useEffect(() => {
    if (!actionsMenuAnchor) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeActionsMenu();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [actionsMenuAnchor, closeActionsMenu]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const { data, isLoading } = useQuery({
    queryKey: ['library-ai-interviews-stats', currentPage, debouncedSearch, categoryFilter],
    queryFn: () =>
      libraryApi.getAIInterviewsStats({
        page: currentPage,
        limit: 20,
        search: debouncedSearch || undefined,
        category: categoryFilter || undefined,
      }),
    staleTime: 2 * 60 * 1000,
  });

  const interviews = data?.interviews || [];
  const pagination = data?.pagination || { current: 1, total: 1, totalCount: 0 };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100/50"
    >
      <div className="w-full px-4 sm:px-6 lg:px-8 pt-6 pb-12">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 rounded-xl bg-[#7c3aed]/10">
              <span className="text-xl">🎤</span>
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
                AI Interview Library
              </h1>
              <p className="text-gray-600 text-sm sm:text-base mt-0.5">
                Browse AI interviews your team has saved from screening flows.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="create-interview-btn"
              onClick={() => setCreateModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#2563eb] text-white text-sm font-medium rounded-lg hover:bg-[#1d4ed8] transition-colors shadow-sm"
            >
              <Plus className="h-5 w-5" />
              Create Interview
            </button>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search by title or description..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-4 pr-4 py-3 bg-white border border-gray-200 rounded-lg shadow-sm text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7c3aed]/30 focus:border-[#7c3aed] hover:border-gray-300 transition-colors duration-200"
            />
            {searchTerm && (
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-500 font-medium tabular-nums">
                {pagination.totalCount} result{pagination.totalCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#7c3aed]/30 focus:border-[#7c3aed] text-gray-700"
            >
              <option value="">All categories</option>
              {['Sales', 'Software Development', 'Marketing', 'General', 'IT', 'Engineering'].map(
                (c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                )
              )}
            </select>
          </div>
        </div>

        {isLoading ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center py-24"
          >
            <div className="h-10 w-10 border-2 border-[#7c3aed] border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-sm font-medium text-gray-600">Loading AI interviews...</p>
          </motion.div>
        ) : interviews.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white rounded-xl border border-gray-200 shadow-sm text-center py-16 px-6"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#7c3aed]/10 mb-4">
              <span className="text-2xl">🎤</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No AI interviews yet</h3>
            <p className="text-gray-500 text-sm max-w-md mx-auto mb-2">
              Save an AI interview from the screening flow to reuse it here.
            </p>
            <button
              type="button"
              data-testid="create-ai-interview-empty-btn"
              onClick={() => setCreateModalOpen(true)}
              className="inline-flex items-center gap-2 mt-4 px-4 py-2.5 bg-[#7c3aed] text-white text-sm font-medium rounded-lg hover:bg-[#6d28d9] transition-colors"
            >
              <Plus className="h-5 w-5" />
              Create AI Interview
            </button>
          </motion.div>
        ) : (
          <>
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-8">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="interview-library-table">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50/80">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Interview
                      </th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Screenings
                      </th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Answered
                      </th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Avg
                      </th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Median
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {interviews.map((row, idx) => (
                      <motion.tr
                        key={row._id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.2, delay: idx * 0.02 }}
                        className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50/50 transition-colors"
                      >
                        <td className="py-4 px-4">
                          <div className="text-left block">
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium text-gray-900">{row.title}</h3>
                              {(() => {
                                const LANGUAGE_BADGES = {
                                  hi: { label: 'Hindi', title: 'Conducted in Hindi' },
                                  hindi: { label: 'Hindi', title: 'Conducted in Hindi' },
                                  'hi-en': { label: 'Hinglish', title: 'Conducted in Hinglish' },
                                  'en-hi': { label: 'Hinglish 2', title: 'Conducted in English, Hindi answers welcome' },
                                  kn: { label: 'Kannada', title: 'Conducted in Kannada' },
                                  ta: { label: 'Tamil', title: 'Conducted in Tamil' },
                                  bn: { label: 'Bengali', title: 'Conducted in Bengali' },
                                  te: { label: 'Telugu', title: 'Conducted in Telugu' },
                                  mr: { label: 'Marathi', title: 'Conducted in Marathi' },
                                  gu: { label: 'Gujarati', title: 'Conducted in Gujarati' },
                                  ml: { label: 'Malayalam', title: 'Conducted in Malayalam' },
                                  pa: { label: 'Punjabi', title: 'Conducted in Punjabi' },
                                  tl: { label: 'Taglish', title: 'Conducted in Tagalog' },
                                  fr: { label: 'French', title: 'Conducted in French' },
                                };
                                const badge = LANGUAGE_BADGES[row.config?.language];
                                return badge ? (
                                  <span
                                    className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800"
                                    title={badge.title}
                                  >
                                    {badge.label}
                                  </span>
                                ) : null;
                              })()}
                              {row.advancedType && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700">
                                  {ADVANCED_INTERVIEW_TYPE_LABELS[row.advancedType] || row.advancedType}
                                </span>
                              )}
                            </div>
                            {row.description && (
                              <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">
                                {row.description}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-4 text-center text-sm text-gray-700">
                          {row.stats?.screeningsCount ?? 0}
                        </td>
                        <td className="py-4 px-4 text-center text-sm text-gray-700">
                          {row.stats?.totalAnswered ?? 0}
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className="inline-flex items-center gap-1 text-sm font-medium text-[#2563eb]">
                            {row.stats?.avgScorePercent != null ? `${row.stats.avgScorePercent}%` : '—'}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700">
                            {row.stats?.medianScorePercent != null ? `${row.stats.medianScorePercent}%` : '—'}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-sm text-gray-600">{row.category || '—'}</td>
                        <td className="py-4 px-4 text-center">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              const menuWidth = 160;
                              const left = Math.min(
                                Math.max(8, rect.right - menuWidth),
                                window.innerWidth - menuWidth - 8
                              );
                              const idStr = String(row._id);
                              setActionsMenuAnchor((cur) =>
                                cur?.id === idStr ? null : { id: idStr, top: rect.bottom + 4, left }
                              );
                            }}
                            className="p-2 rounded-lg text-gray-500 hover:text-[#2563eb] hover:bg-[#2563eb]/10 transition-colors"
                            title="Actions"
                            aria-label="Open actions menu"
                            aria-expanded={actionsMenuAnchor?.id === String(row._id)}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {actionsMenuAnchor &&
              createPortal(
                (() => {
                  const rowForMenu = interviews.find((a) => String(a._id) === actionsMenuAnchor.id);
                  return (
                    <>
                      <div className="fixed inset-0 z-[199]" aria-hidden onClick={closeActionsMenu} />
                      <div
                        className="fixed z-[200] w-40 rounded-lg border border-gray-200 bg-white py-1 shadow-xl text-left"
                        style={{ top: actionsMenuAnchor.top, left: actionsMenuAnchor.left }}
                        role="menu"
                      >
                        <button
                          type="button"
                          className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => {
                            setViewInterviewId(actionsMenuAnchor.id);
                            closeActionsMenu();
                          }}
                        >
                          <Eye className="h-4 w-4 shrink-0" />
                          View
                        </button>
                        <button
                          type="button"
                          className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => {
                            // Route rubric-based templates to RubricInterviewCreate
                            const rowForEdit = interviews.find((a) => String(a._id) === actionsMenuAnchor.id);
                            if (rowForEdit?.advancedType === 'rubric-based') {
                              setEditRubricTemplateId(actionsMenuAnchor.id);
                            } else {
                              setEditLibraryInterviewId(actionsMenuAnchor.id);
                            }
                            closeActionsMenu();
                          }}
                        >
                          <Pencil className="h-4 w-4 shrink-0" />
                          Edit
                        </button>
                        {canDeleteLibraryAIInterview(rowForMenu) && (
                          <button
                            type="button"
                            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-rose-600 hover:bg-rose-50"
                            onClick={() => {
                              setDeleteConfirm({
                                id: actionsMenuAnchor.id,
                                title: rowForMenu?.title || 'this AI interview',
                              });
                              closeActionsMenu();
                            }}
                          >
                            <TrashIcon className="h-4 w-4" />
                            <span>Delete</span>
                          </button>
                        )}
                      </div>
                    </>
                  );
                })(),
                document.body
              )}

            {deleteConfirm &&
              createPortal(
                <div className="fixed inset-0 z-[210] flex items-center justify-center px-4">
                  <div
                    className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
                    onClick={() => !deleteLibraryMutation.isPending && setDeleteConfirm(null)}
                    aria-hidden
                  />
                  <div
                    className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="delete-library-ai-interview-title"
                  >
                    <h2 id="delete-library-ai-interview-title" className="text-lg font-semibold text-gray-900">
                      Delete from library?
                    </h2>
                    <p className="mt-1 text-sm font-medium text-gray-800 line-clamp-2">
                      “{deleteConfirm.title}”
                    </p>
                    <p className="mt-3 text-sm text-gray-600 leading-relaxed">
                      This cannot be undone. You won’t see this template again or its usage stats here.{' '}
                      <span className="font-medium text-gray-800">Active screenings already using it</span> are not
                      affected.
                    </p>
                    <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3">
                      <button
                        type="button"
                        className="px-4 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        disabled={deleteLibraryMutation.isPending}
                        onClick={() => setDeleteConfirm(null)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="px-4 py-2.5 rounded-lg bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 disabled:opacity-50"
                        disabled={deleteLibraryMutation.isPending}
                        onClick={() => deleteLibraryMutation.mutate(deleteConfirm.id)}
                      >
                        {deleteLibraryMutation.isPending ? 'Deleting…' : 'Yes, delete'}
                      </button>
                    </div>
                  </div>
                </div>,
                document.body
              )}

            {/* Pagination */}
            {pagination.total > 1 && (
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>
                  Page {pagination.current} of {pagination.total}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={pagination.current <= 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={pagination.current >= pagination.total}
                    onClick={() => setCurrentPage((p) => Math.min(pagination.total, p + 1))}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        <AIInterviewViewModal
          open={Boolean(viewInterviewId)}
          interviewId={viewInterviewId}
          onClose={() => setViewInterviewId(null)}
        />

        {createModalOpen && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center px-4 transition-opacity duration-200 bg-black/40 backdrop-blur-[2px]">
            <div
              className="fixed inset-0 transition-opacity"
              aria-hidden
            />
            <div
              className="relative flex flex-col w-full max-w-4xl h-[70vh] max-h-[70vh] bg-white rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ease-out"
              style={{ minHeight: '580px' }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <ErrorBoundary fallbackMessage="The AI Interview Create modal crashed. Please check the console for details.">
                <AIInterviewCreate
                  isModal
                  skipInviteStep
                  enableTypeSelection
                  enableCreationModeSelection
                  enableCreatorSelection
                  onClose={() => {
                    setCreateModalOpen(false);
                    queryClient.invalidateQueries({ queryKey: ['library-ai-interviews-stats'] });
                  }}
                />
              </ErrorBoundary>
            </div>
          </div>
        )}

        {editLibraryInterviewId && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center px-4 transition-opacity duration-200 bg-black/40 backdrop-blur-[2px]">
            <div
              className="fixed inset-0 transition-opacity"
              aria-hidden
            />
            <div
              className="relative flex flex-col w-full max-w-4xl h-[70vh] max-h-[70vh] bg-white rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ease-out"
              style={{ minHeight: '580px' }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <AIInterviewCreate
                isModal
                editLibraryInterviewId={editLibraryInterviewId}
                onClose={() => {
                  setEditLibraryInterviewId(null);
                  queryClient.invalidateQueries({ queryKey: ['library-ai-interviews-stats'] });
                }}
              />
            </div>
          </div>
        )}


        {editRubricTemplateId && (
          <RubricInterviewCreate
            editTemplateId={editRubricTemplateId}
            onClose={() => {
              setEditRubricTemplateId(null);
              queryClient.invalidateQueries({ queryKey: ['library-ai-interviews-stats'] });
            }}
            onSaved={() => {
              queryClient.invalidateQueries({ queryKey: ['library-ai-interviews-stats'] });
            }}
          />
        )}
      </div>
    </motion.div>
  );
};

export default AIInterviewLibrary;

